import { supabase } from './supabase';
import type { ApiResponse } from './appConfigService';
import { isCrashLikeEvent } from '../utils/crashMonitor';

export type AppErrorLevel = 'error' | 'warning' | 'info';

export interface AppErrorEvent {
  id: string;
  created_at: string;
  user_id?: string | null;
  session_id?: string | null;
  level: AppErrorLevel;
  message: string;
  stack?: string | null;
  source?: string | null;
  url?: string | null;
  user_agent?: string | null;
  platform?: string | null;
  app_version?: string | null;
  tags?: Record<string, unknown> | null;
  context?: Record<string, unknown> | null;
}

export type AppErrorEventInsert = Omit<AppErrorEvent, 'id' | 'created_at'>;

export const appErrorMonitorService = {
  async logEvent(payload: AppErrorEventInsert): Promise<ApiResponse<null>> {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const payloadWithUser: AppErrorEventInsert = {
        ...payload,
        user_id: payload.user_id ?? authData.user?.id ?? null,
      };

      const { error } = await supabase.from('app_error_events').insert([payloadWithUser]);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to log app error event.' };
    }
  },

  async getEventsPage(
    limit = 50,
    offset = 0,
    options?: { level?: AppErrorLevel; search?: string },
  ): Promise<ApiResponse<AppErrorEvent[]> & { count?: number | null }> {
    try {
      let query = supabase
        .from('app_error_events')
        .select(
          'id, created_at, user_id, session_id, level, message, stack, source, url, user_agent, platform, app_version, tags, context',
          { count: 'exact' },
        )
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (options?.level) {
        query = query.eq('level', options.level);
      }
      if (options?.search) {
        query = query.ilike('message', `%${options.search}%`);
      }

      const { data, error, count } = await query;
      if (error) {
        return { success: false, message: error.message, count };
      }
      return { success: true, data: data ?? [], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load app error events.' };
    }
  },

  async getCrashEvents(
    limit = 500,
    options?: { platform?: string },
  ): Promise<ApiResponse<AppErrorEvent[]>> {
    try {
      let query = supabase
        .from('app_error_events')
        .select(
          'id, created_at, user_id, session_id, level, message, stack, source, url, user_agent, platform, app_version, tags, context',
        )
        .order('created_at', { ascending: false })
        .limit(Math.max(limit * 2, 200));

      if (options?.platform) {
        query = query.eq('platform', options.platform);
      }

      const { data, error } = await query;
      if (error) {
        return { success: false, message: error.message };
      }

      return {
        success: true,
        data: (data ?? []).filter(isCrashLikeEvent).slice(0, limit),
      };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load crash events.' };
    }
  },

  async getRecentSevereEvents(limit = 500): Promise<ApiResponse<AppErrorEvent[]>> {
    try {
      const { data, error } = await supabase
        .from('app_error_events')
        .select(
          'id, created_at, user_id, session_id, level, message, stack, source, url, user_agent, platform, app_version, tags, context',
        )
        .in('level', ['error', 'warning'])
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: data ?? [] };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load recent app error events.' };
    }
  },

  async clearEvents(): Promise<ApiResponse<null>> {
    try {
      const { error } = await supabase.from('app_error_events').delete().not('id', 'is', null);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to clear app error events.' };
    }
  },
};
